import { useParams, Link } from 'react-router-dom'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { useState } from 'react'
import { Card } from '@magnetic/card'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Breadcrumb } from '@magnetic/breadcrumb'
import { Skeleton } from '@magnetic/skeleton'
import { Button } from '@magnetic/button'
import { Col, Row } from '@magnetic/grid'
import { Notification } from '@magnetic/notification'
import { ArrowLeft, Negative } from '@/lib/icons'
import { getApplication, getProject } from '@/lib/api'
import { ApplicationActivityTimeline } from '@/components/application-activity-timeline'
import { ApplicationTrafficMetrics } from '@/components/application-traffic-metrics'
import { ApplicationHeader } from '@/components/application/ApplicationHeader'
import { ApplicationStateCard } from '@/components/application/ApplicationStateCard'
import { ApplicationSetupAttemptsCard } from '@/components/application/ApplicationSetupAttemptsCard'
import { StatusCards } from '@/components/application/StatusCards'
import { ResourceDiff } from '@/components/application/ResourceDiff'
import { ImageDeploymentSection } from '@/components/application/ImageDeploymentSection'
import { ApplicationLogsModal } from '@/components/modals/ApplicationLogsModal'
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal'
import { ForceDeleteModal } from '@/components/modals/ForceDeleteModal'
import { EcrGitHubActionsModal } from '@/components/modals/EcrGitHubActionsModal'
import { useApplicationLogs } from '@/hooks/useApplicationLogs'
import { useApplicationActions } from '@/hooks/useApplicationActions'
import { useSyncPolicy } from '@/hooks/useSyncPolicy'
import { useApplicationStatusStream } from '@/hooks/useApplicationStatusStream'
import { timestampToDate } from '@/lib/protobuf-utils'

export function ApplicationDetailPage() {
  const { projectId, appId } = useParams()

  // Data fetching
  const { data: applicationResponse, isLoading, error } = useConnectQuery(
    getApplication,
    { id: appId! },
    {
      enabled: !!appId,
      refetchInterval: 30000,
    }
  )
  const { data: projectResponse } = useConnectQuery(
    getProject,
    { id: projectId! },
    { enabled: !!projectId }
  )

  const application = applicationResponse?.application
  const project = projectResponse?.project

  const shouldStream = !!application?.state && [
    'Created',
    'ValidatingHelm',
    'CreatingCRD',
    'Syncing',
  ].includes(application.state)

  const stream = useApplicationStatusStream(appId!, { enabled: shouldStream })
  const streamStatus = shouldStream ? stream.status : null
  const streamUpdatedAt = streamStatus?.updatedAt ? timestampToDate(streamStatus.updatedAt) : null
  const appUpdatedAt = application?.stateUpdatedAt ? timestampToDate(application.stateUpdatedAt) : null
  const shouldUseStream = !!streamStatus && (!!streamUpdatedAt && (!appUpdatedAt || streamUpdatedAt > appUpdatedAt))

  const effectiveApplication = application
    ? {
        ...application,
        state: shouldUseStream ? streamStatus?.state : application.state,
        stateMessage: shouldUseStream ? streamStatus?.stateMessage : application.stateMessage,
        stateUpdatedAt: shouldUseStream ? streamStatus?.updatedAt : application.stateUpdatedAt,
      }
    : application

  // Extracted hooks
  const logs = useApplicationLogs(appId!)
  const actions = useApplicationActions(appId!, projectId!)
  const syncPolicy = useSyncPolicy(effectiveApplication, appId!)

  // Modal state
  const [activeModal, setActiveModal] = useState<'delete' | 'forceDelete' | null>(null)
  const [ecrSetupModalOpen, setEcrSetupModalOpen] = useState(false)

  // Loading state
  if (isLoading) {
    return (
      <Flex direction="vertical" gap="lg">
        {/* Breadcrumb skeleton */}
        <Skeleton height="sm" style={{ width: 300 }} />

        {/* Header skeleton with status badges */}
        <Flex align="center" justify="space-between">
          <Flex direction="vertical" gap="sm">
            <Flex align="center" gap={8}>
              <Skeleton height="lg" shade="dark" style={{ width: 250 }} />
              <Skeleton height={24} style={{ width: 24, borderRadius: '50%' }} />
              <Skeleton height="sm" style={{ width: 80 }} />
              <Skeleton height={24} style={{ width: 24, borderRadius: '50%' }} />
              <Skeleton height="sm" style={{ width: 80 }} />
            </Flex>
            <Skeleton height="sm" style={{ width: 200 }} />
          </Flex>
          <Flex gap="sm">
            <Skeleton height={36} style={{ width: 100 }} />
            <Skeleton height={36} style={{ width: 100 }} />
          </Flex>
        </Flex>

        {/* Content skeleton - status cards and metadata */}
        <Card.Group>
          <Card>
            <Flex direction="vertical" gap={12}>
              <Skeleton height="md" shade="dark" style={{ width: 120 }} />
              <Skeleton height="sm" lines={2} />
            </Flex>
          </Card>
          <Card>
            <Flex direction="vertical" gap={12}>
              <Skeleton height="md" shade="dark" style={{ width: 100 }} />
              <Skeleton height="sm" lines={3} oblique />
            </Flex>
          </Card>
        </Card.Group>
      </Flex>
    )
  }

  // Error state
  if (error || !effectiveApplication) {
    return (
      <Flex direction="vertical" align="center" gap="md" style={{ padding: 48 }}>
        <Negative width={48} height={48} style={{ marginBottom: 16 }} />
        <Heading size="sub-section">Failed to load application</Heading>
        <Text size="p4" color="light">
          {error instanceof Error ? error.message : 'Application not found'}
        </Text>
        <Link to={`/projects/${projectId}`}>
          <Button kind="secondary" icon={<ArrowLeft size={16} />}>
            Back to Project
          </Button>
        </Link>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap="lg">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Link href="/projects">Projects</Breadcrumb.Link>
        <Breadcrumb.Link href={`/projects/${projectId}`}>Project</Breadcrumb.Link>
        <Breadcrumb.Link isLast>{effectiveApplication.displayName || effectiveApplication.name}</Breadcrumb.Link>
      </Breadcrumb>

      {/* Header */}
      <ApplicationHeader
        application={effectiveApplication}
        project={project}
        onSync={actions.handleSync}
        onSuspend={actions.handleSuspend}
        onResume={actions.handleResume}
        onDelete={() => setActiveModal('delete')}
        onViewLogs={() => logs.setLogsOpen(true)}
        onViewEcrSetup={project?.awsRoleArn ? () => setEcrSetupModalOpen(true) : undefined}
        isLoading={{
          sync: actions.isLoading.sync,
          suspend: actions.isLoading.suspend,
          resume: actions.isLoading.resume,
          delete: actions.isLoading.delete,
        }}
        logsLoading={logs.logsOpen && logs.logsStreaming}
      />

      {/* Main Content */}
      <Row>
      {effectiveApplication.imageDeploymentStatus === 'failed' && effectiveApplication.imageDeploymentMessage && (
          <Col span={6} sm={12}>
            <Notification status="negative" title="Image Deployment Failed">
              <Flex direction="vertical" gap="sm">
                <Text size="p3">{effectiveApplication.imageDeploymentMessage}</Text>
                <Text size="p4">
                  The container image deployment to ECR failed. Contact support for assistance.
                </Text>
              </Flex>
            </Notification>
          </Col>
        )}

        <Col span={6} sm={12}>
          <ApplicationStateCard
            application={effectiveApplication}
            onForceDelete={() => setActiveModal('forceDelete')}
            onReconcileCRD={actions.handleReconcileCRD}
            forceDeletePending={actions.isLoading.forceDelete}
            reconcileCRDPending={actions.isLoading.reconcileCRD}
          />
        </Col>

        <Col span={6} sm={12}>
          <ApplicationActivityTimeline
            application={effectiveApplication}
            onRetryHelm={actions.handleRetryHelm}
            onRetryCRD={actions.handleRetryCRD}
            onRetrySetup={actions.handleRetrySetup}
            onStartValidation={actions.handleStartValidation}
            isRetrying={
              actions.isLoading.retryHelm ||
              actions.isLoading.retryCRD ||
              actions.isLoading.retrySetup
            }
        isStartingValidation={actions.isLoading.startValidation}
          />
        </Col>
      </Row>

      <ApplicationSetupAttemptsCard applicationId={appId!} />

      {/* Status, Source, Sync Policy */}
      <StatusCards
        application={effectiveApplication}
        syncPolicy={syncPolicy.syncPolicy}
        onUpdateSyncPolicy={syncPolicy.updateSyncPolicy}
        isPending={syncPolicy.isPending}
      />

      {/* Image Deployment Section - Always visible */}
      <ImageDeploymentSection application={effectiveApplication} applicationId={appId!} />

      {/* Traffic & Resource Status */}
      <Flex direction="vertical" gap="md">
        <Heading size="section">Traffic & Health</Heading>
      <ApplicationTrafficMetrics
        applicationId={appId!}
        applicationName={effectiveApplication.name}
      />
      </Flex>

      {/* Resource Diff */}
      <ResourceDiff applicationId={appId!} />

      {/* Modals */}
      <ApplicationLogsModal
        isOpen={logs.logsOpen}
        onClose={() => logs.setLogsOpen(false)}
        logs={logs.logs}
        logsError={logs.logsError}
        logsStreaming={logs.logsStreaming}
        followLogs={logs.followLogs}
        setFollowLogs={logs.setFollowLogs}
        tailLines={logs.tailLines}
        setTailLines={logs.setTailLines}
        logsFullScreen={logs.logsFullScreen}
        setLogsFullScreen={logs.setLogsFullScreen}
        activePod={logs.activePod}
        activeContainer={logs.activeContainer}
        startLogsStream={logs.startLogsStream}
        clearLogs={logs.clearLogs}
        logsContainerRef={logs.logsContainerRef}
      />

      <DeleteConfirmationModal
        isOpen={activeModal === 'delete'}
        onClose={() => setActiveModal(null)}
        onConfirm={actions.handleDelete}
        applicationName={effectiveApplication.displayName || effectiveApplication.name}
        isPending={actions.isLoading.delete}
      />

      <ForceDeleteModal
        isOpen={activeModal === 'forceDelete'}
        onClose={() => setActiveModal(null)}
        onConfirm={actions.handleForceDelete}
        applicationName={effectiveApplication.displayName || effectiveApplication.name}
        isPending={actions.isLoading.forceDelete}
      />

      <EcrGitHubActionsModal
        isOpen={ecrSetupModalOpen}
        onClose={() => setEcrSetupModalOpen(false)}
        project={project}
        application={effectiveApplication}
      />
    </Flex>
  )
}
